import React from 'react'

export type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'bracketed'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: React.ReactNode
}

const variantStyles: Record<Exclude<ButtonVariant, 'bracketed'>, React.CSSProperties> = {
  primary: {
    backgroundColor: 'var(--color-accent)',
    color: 'var(--color-paper)',
    border: '1px solid var(--color-accent)',
  },
  ghost: {
    backgroundColor: 'transparent',
    color: 'var(--color-ink)',
    border: '1px solid var(--color-border)',
  },
  danger: {
    backgroundColor: 'transparent',
    color: '#C0392B',
    border: '1px solid #C0392B',
  },
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '4px 12px', fontSize: '0.8125rem' },
  md: { padding: '6px 16px', fontSize: '0.875rem' },
  lg: { padding: '8px 20px', fontSize: '1rem' },
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'ghost', size = 'md', loading = false, children, style, disabled, ...props }, ref) => {
    if (variant === 'bracketed') {
      return (
        <button
          ref={ref}
          disabled={disabled || loading}
          style={{
            background: 'none',
            border: 'none',
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.6875rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--color-accent)',
            padding: '4px 0',
            opacity: disabled || loading ? 0.5 : 1,
            transition: 'opacity 0.15s',
            ...style,
          }}
          {...props}
        >
          <span style={{ color: 'var(--color-ink-faint)' }}>[ </span>
          {loading ? '...' : children}
          <span style={{ color: 'var(--color-ink-faint)' }}> ]</span>
        </button>
      )
    }

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-serif)',
          fontWeight: 500,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          transition: 'opacity 0.15s ease, background 0.15s ease',
          opacity: disabled || loading ? 0.6 : 1,
          ...variantStyles[variant],
          ...sizeStyles[size],
          ...style,
        }}
        {...props}
      >
        {loading ? '...' : children}
      </button>
    )
  }
)

Button.displayName = 'Button'
